// Image export for custom-dashboard widgets.
//
// Chart widgets (bar/line/pie/map) render as a single ECharts SVG, so they can be
// exported as true vector SVG and rasterized to JPEG straight from that SVG. HTML-based
// widgets (table, metric card, card grid, etc.) have no full-widget vector source, so
// they are rasterized to JPEG with html2canvas.

const SVG_NS = 'http://www.w3.org/2000/svg';
const RASTER_SCALE = 2; // supersample so raster exports stay crisp
const JPEG_QUALITY = 0.95;

/** Strip characters that are illegal in filenames and cap the length. */
export const sanitizeFilename = (name: string): string => {
  const cleaned = (name || 'widget')
    .trim()
    .replace(/[\\/:*?"<>|\n\r\t]+/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 120)
    .trim();
  return cleaned || 'widget';
};

const triggerDownload = (href: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/** Clone an ECharts SVG with an explicit size, namespaces, and an opaque white background. */
const prepareSvgClone = (svg: SVGSVGElement): { clone: SVGSVGElement; width: number; height: number } => {
  const rect = svg.getBoundingClientRect();
  const width = rect.width || parseFloat(svg.getAttribute('width') || '0') || 0;
  const height = rect.height || parseFloat(svg.getAttribute('height') || '0') || 0;

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  if (width) clone.setAttribute('width', String(width));
  if (height) clone.setAttribute('height', String(height));

  // ECharts SVG is transparent — add an opaque white background so the exported
  // file isn't see-through when opened in an image viewer.
  const bg = document.createElementNS(SVG_NS, 'rect');
  bg.setAttribute('x', '0');
  bg.setAttribute('y', '0');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  return { clone, width, height };
};

/**
 * Export the widget's ECharts SVG as a standalone .svg file.
 * Throws if no <svg> is present (caller should only offer SVG for chart widgets).
 */
export const downloadWidgetAsSVG = (node: HTMLElement, name: string): void => {
  const svg = node.querySelector('svg');
  if (!svg) {
    throw new Error('No SVG element found in widget');
  }

  const { clone } = prepareSvgClone(svg as SVGSVGElement);
  const svgData = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${svgData}`], {
    type: 'image/svg+xml;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${sanitizeFilename(name)}.svg`);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Rasterize a single ECharts SVG to JPEG via an <img> + canvas (reliable for inline SVG). */
const rasterizeSvgToJpeg = (svg: SVGSVGElement, name: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const { clone, width, height } = prepareSvgClone(svg);
    if (!width || !height) {
      reject(new Error('SVG has no measurable size'));
      return;
    }

    const svgData = new XMLSerializer().serializeToString(clone);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`;

    const img = new Image();
    img.onload = () => {
      // onload runs on the event loop, so a throw here would escape the Promise and
      // leave it pending forever — catch and reject so callers always settle.
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * RASTER_SCALE));
        canvas.height = Math.max(1, Math.round(height * RASTER_SCALE));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        triggerDownload(canvas.toDataURL('image/jpeg', JPEG_QUALITY), `${sanitizeFilename(name)}.jpeg`);
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => reject(new Error('Failed to load SVG for rasterization'));
    img.src = url;
  });

/**
 * Rasterize a widget to JPEG.
 * `vector: true` marks a chart widget rendered as a single ECharts SVG — that SVG is
 * rasterized directly (html2canvas renders inline SVG unreliably). All other widgets
 * fall back to html2canvas, which is loaded lazily to stay out of the main bundle.
 */
export const downloadWidgetAsJPEG = async (
  node: HTMLElement,
  name: string,
  opts: { vector?: boolean } = {},
): Promise<void> => {
  if (opts.vector) {
    const svg = node.querySelector('svg');
    if (svg) {
      await rasterizeSvgToJpeg(svg as SVGSVGElement, name);
      return;
    }
  }

  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(node, {
    backgroundColor: '#ffffff',
    scale: RASTER_SCALE,
    useCORS: true,
    logging: false,
  });
  triggerDownload(canvas.toDataURL('image/jpeg', JPEG_QUALITY), `${sanitizeFilename(name)}.jpeg`);
};
